import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import { TaskStatus } from '@dealstack/shared/lib/generated';
import {
	getShareRegisterPdfTaskStatus,
	startShareRegisterPdfGeneration,
} from '@dealstack/frontend/app/services/Holdings.service.ts';
import { downloadUrlAsFile } from '@dealstack/frontend/shared/files/downloadFile';

interface Props {
	/** Name to save the PDF under; falls back to the server's file name. */
	fileName?: string;
	onDone?: () => void;
	onError?: () => void;
}

const TERMINAL_STATUSES: ReadonlySet<TaskStatus> = new Set([
	TaskStatus.DONE,
	TaskStatus.ERROR,
	TaskStatus.CANCELLED,
]);

export const useShareRegisterPdfGeneration = ({ fileName, onDone, onError }: Props = {}) => {
	const [taskId, setTaskId] = useState<string | undefined>();
	// Refs, not state — Strict-Mode double-invoke would otherwise fire the
	// download / onError twice before state settled.
	const downloadTriggered = useRef(false);
	const errorTriggered = useRef(false);
	// Refs hold the latest callbacks so the effect doesn't re-run on inline arrows.
	const onDoneRef = useRef(onDone);
	const onErrorRef = useRef(onError);
	onDoneRef.current = onDone;
	onErrorRef.current = onError;
	const fileNameRef = useRef(fileName);
	fileNameRef.current = fileName;

	const startMutation = useMutation({
		mutationFn: startShareRegisterPdfGeneration,

		onSuccess: ({ taskId: newTaskId }) => {
			downloadTriggered.current = false;
			errorTriggered.current = false;
			setTaskId(newTaskId);
		},
	});

	const statusEndpoint = taskId
		? formatUnicorn(API_PATHS.CAP_SHARE_REGISTER_PDF_TASK_TASKID_STATUS_GET, { taskId })
		: '';

	const statusQuery = useQuery({
		queryKey: [statusEndpoint],
		queryFn: () => getShareRegisterPdfTaskStatus(statusEndpoint),
		enabled: !!taskId,
		refetchInterval: (query) => {
			const data = query.state.data;
			if ((data && TERMINAL_STATUSES.has(data.status)) || !!query.state.error) {
				return false;
			}
			// 1s catches fast generations; back off so multi-minute jobs don't hammer.
			return query.state.dataUpdateCount < 10 ? 1000 : 3000;
		},
	});

	useEffect(() => {
		const data = statusQuery.data;
		if (!data) {
			return;
		}

		if (data.status === TaskStatus.DONE && data.downloadUrl && !downloadTriggered.current) {
			downloadTriggered.current = true;
			void downloadUrlAsFile(
				data.downloadUrl,
				fileNameRef.current ?? data.fileName ?? 'share-register.pdf'
			);
			onDoneRef.current?.();
		}

		if (
			(data.status === TaskStatus.ERROR || data.status === TaskStatus.CANCELLED) &&
			!errorTriggered.current
		) {
			errorTriggered.current = true;
			onErrorRef.current?.();
		}
	}, [statusQuery.data]);

	const reset = () => {
		setTaskId(undefined);
		downloadTriggered.current = false;
		errorTriggered.current = false;
	};

	return {
		start: startMutation.mutate,
		isStarting: startMutation.isPending,
		startError: startMutation.error,
		status: statusQuery.data,
		isPolling:
			!!taskId && !!statusQuery.data && !TERMINAL_STATUSES.has(statusQuery.data.status),
		reset,
	};
};
